'use client';

  import { useEffect, useRef } from 'react';
  import { usePreloader } from '@/contexts/PreloaderContext';

  export default function SoccerPreloader() {
    const { isVisible, label } = usePreloader();
    const pctRef = useRef<HTMLSpanElement>(null);
    const rafRef = useRef<number>(0);

    useEffect(() => {
      if (!isVisible) {
        cancelAnimationFrame(rafRef.current);
        return;
      }
      const dur = 3000;
      let start: number | null = null;
      const tick = (ts: number) => {
        if (!start) start = ts;
        const elapsed = (ts - start) % dur;
        const p = Math.min(Math.round((elapsed / (dur * 0.85)) * 100), 100);
        if (pctRef.current) pctRef.current.textContent = p + '%';
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafRef.current);
    }, [isVisible]);

    if (!isVisible) return null;

    return (
      <>
        <style>{`
          .pl-overlay {
            position: fixed; inset: 0; z-index: 9999;
            background-color: #0d1117;
            display: flex; flex-direction: column;
            align-items: center; justify-content: center;
            font-family: 'DM Sans', sans-serif;
            overflow: hidden;
          }
          .pl-overlay::before {
            content: '';
            position: fixed; inset: 0;
            background-image:
              repeating-linear-gradient(90deg, transparent, transparent 79px, hsla(210,30%,50%,0.04) 79px, hsla(210,30%,50%,0.04) 80px),
              repeating-linear-gradient(0deg,  transparent, transparent 79px, hsla(210,30%,50%,0.04) 79px, hsla(210,30%,50%,0.04) 80px);
            pointer-events: none;
          }
          .pl-glow {
            position: absolute; width: 340px; height: 340px; border-radius: 50%;
            background: radial-gradient(circle, hsla(344,75%,35%,0.18) 0%, transparent 70%);
            pointer-events: none;
            animation: pl-glow-pulse 3s ease-in-out infinite;
          }
          @keyframes pl-glow-pulse {
            0%, 100% { transform: scale(1); opacity: 0.7; }
            50%       { transform: scale(1.15); opacity: 1; }
          }
          .pl-card {
            position: relative; display: flex; flex-direction: column;
            align-items: center; gap: 2rem;
            padding: 3rem 3.5rem;
            background: hsla(210,15%,10%,0.6);
            border: 1px solid hsla(210,20%,30%,0.25);
            border-radius: 24px;
            backdrop-filter: blur(20px);
            box-shadow: 0 0 0 1px hsla(344,75%,40%,0.08), 0 32px 64px -16px rgba(0,0,0,0.6);
          }
          .pl-stripe-bar {
            width: 100%; height: 2px; border-radius: 1px; opacity: 0.6;
            background: linear-gradient(90deg,
              hsl(213,81%,21%) 0%, hsl(213,81%,21%) 33%,
              white 33%, white 66%,
              hsl(344,75%,40%) 66%, hsl(344,75%,40%) 100%
            );
          }
          .pl-label { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
          .pl-title {
            font-family: 'Bebas Neue', 'DM Sans', sans-serif;
            font-size: 1.75rem; letter-spacing: 0.18em;
            color: white; text-transform: uppercase;
          }
          .pl-subtitle {
            font-size: 0.75rem; font-weight: 300; letter-spacing: 0.22em;
            color: hsl(210,15%,35%); text-transform: uppercase;
          }
          .pl-dots span { animation: pl-blink 1.4s infinite; opacity: 0; }
          .pl-dots span:nth-child(2) { animation-delay: 0.2s; }
          .pl-dots span:nth-child(3) { animation-delay: 0.4s; }
          @keyframes pl-blink {
            0%, 80%, 100% { opacity: 0; } 40% { opacity: 1; }
          }
          .pl-progress-wrap { width: 100%; display: flex; flex-direction: column; gap: 0.5rem; }
          .pl-progress-meta {
            display: flex; justify-content: space-between;
            font-size: 0.68rem; letter-spacing: 0.1em;
            color: hsl(210,15%,35%); text-transform: uppercase;
          }
          .pl-progress-track {
            width: 100%; height: 3px;
            background: hsla(210,20%,30%,0.4);
            border-radius: 99px; overflow: hidden;
          }
          .pl-progress-fill {
            height: 100%; width: 0%;
            background: linear-gradient(90deg, hsl(213,81%,21%), hsl(344,75%,40%));
            border-radius: 99px;
            animation: pl-fill-progress 3s cubic-bezier(0.4,0,0.6,1) infinite;
            box-shadow: 0 0 8px hsla(344,75%,50%,0.6);
          }
          @keyframes pl-fill-progress {
            0%   { width: 0%; }
            85%  { width: 100%; }
            100% { width: 100%; opacity: 0; }
          }
          .pl svg { --dur: 3s; display: block; }
          .pl__ball, .pl__ball-shadow, .pl__ball-texture,
          .pl__stripe, .pl__stripe-dot, .pl__stripe-rotate {
            animation-duration: var(--dur);
            animation-timing-function: linear;
            animation-iteration-count: infinite;
          }
          .pl__ball          { animation-name: pl__ball; }
          .pl__ball-shadow   { animation-name: pl__ball-shadow; }
          .pl__ball-texture  { animation-name: pl__ball-texture; }
          .pl__stripe-dot    { animation-name: pl__stripe-dot; }
          .pl__stripe-rotate { animation-name: pl__stripe-rotate; }
          .pl__stripe--1     { animation-name: pl__stripe1; }
          .pl__stripe--2     { animation-name: pl__stripe2; }
          .pl__stripe--3     { animation-name: pl__stripe3; }
          .pl__stripe-dot-group:nth-child(1) .pl__stripe-dot:nth-child(2) { animation-delay: calc(var(--dur) * 0.2333); }
          .pl__stripe-dot-group:nth-child(1) .pl__stripe-dot:nth-child(3) { animation-delay: calc(var(--dur) * 0.3); }
          .pl__stripe-dot-group:nth-child(1) .pl__stripe-dot:nth-child(4) { animation-delay: calc(var(--dur) * 0.45); }
          .pl__stripe-dot-group:nth-child(1) .pl__stripe-dot:nth-child(5) { animation-delay: calc(var(--dur) * 0.6833); }
          .pl__stripe-dot-group:nth-child(1) .pl__stripe-dot:nth-child(6) { animation-delay: calc(var(--dur) * 0.7083); }
          .pl__stripe-dot-group:nth-child(1) .pl__stripe-dot:nth-child(7) { animation-delay: calc(var(--dur) * 0.8417); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(1) { animation-delay: calc(var(--dur) * 0.1083); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(2) { animation-delay: calc(var(--dur) * 0.3333); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(3) { animation-delay: calc(var(--dur) * 0.425); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(4) { animation-delay: calc(var(--dur) * 0.6); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(5) { animation-delay: calc(var(--dur) * 0.6583); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(6) { animation-delay: calc(var(--dur) * 0.8083); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(7) { animation-delay: calc(var(--dur) * 0.8417); }
          .pl__stripe-dot-group:nth-child(2) .pl__stripe-dot:nth-child(8) { animation-delay: calc(var(--dur) * 0.8917); }
          .pl__stripe-dot-group:nth-child(3) .pl__stripe-dot:nth-child(2) { animation-delay: calc(var(--dur) * 0.0583); }
          .pl__stripe-dot-group:nth-child(3) .pl__stripe-dot:nth-child(3) { animation-delay: calc(var(--dur) * 0.2083); }
          .pl__stripe-dot-group:nth-child(3) .pl__stripe-dot:nth-child(4) { animation-delay: calc(var(--dur) * 0.4); }
          .pl__stripe-dot-group:nth-child(3) .pl__stripe-dot:nth-child(5) { animation-delay: calc(var(--dur) * 0.5167); }
          .pl__stripe-dot-group:nth-child(3) .pl__stripe-dot:nth-child(6) { animation-delay: calc(var(--dur) * 0.625); }
          .pl__stripe-dot-group:nth-child(3) .pl__stripe-dot:nth-child(7) { animation-delay: calc(var(--dur) * 0.7667); }
          @keyframes pl__ball {
            from { transform: rotate(0) translate(0,-15.75px); }
            to   { transform: rotate(1turn) translate(0,-15.75px); }
          }
          @keyframes pl__ball-shadow {
            from { transform: rotate(0); }
            to   { transform: rotate(-1turn); }
          }
          @keyframes pl__ball-texture {
            from { transform: translate(-16px,0); }
            to   { transform: translate(48px,0); }
          }
          @keyframes pl__stripe-dot {
            from       { r: 1.25px; }
            16.67%, to { r: 0; }
          }
          @keyframes pl__stripe-rotate {
            from { transform: rotate(0); }
            to   { transform: rotate(1turn); }
          }
          @keyframes pl__stripe1 {
            from, to { stroke-dashoffset: -95.77; }
            50% { animation-timing-function: cubic-bezier(0.65,0,0.35,1); stroke-dashoffset: -75.70; }
          }
          @keyframes pl__stripe2 {
            from, to { stroke-dashoffset: -80.1; }
            50% { animation-timing-function: cubic-bezier(0.65,0,0.35,1); stroke-dashoffset: -53.4; }
          }
          @keyframes pl__stripe3 {
            from, to { stroke-dashoffset: -72.765; }
            50% { animation-timing-function: cubic-bezier(0.65,0,0.35,1); stroke-dashoffset: -48.51; }
          }
        `}</style>

        <div className="pl-overlay">
          <div className="pl-glow" />
          <div className="pl-card">
            <div className="pl">
              <svg viewBox="0 0 56 56" width="160" height="160" role="img" aria-label="Loading">
                <clipPath id="ball-clip"><circle r="8" /></clipPath>
                <defs>
                  <path id="hex" d="M 0 -9.196 L 8 -4.577 L 8 4.661 L 0 9.28 L -8 4.661 L -8 -4.577 Z" />
                  <g id="hex-chunk" fill="none" stroke="hsl(210,10%,10%)" strokeWidth="0.5">
                    <use href="#hex" fill="hsl(210,10%,10%)" />
                    <use href="#hex" transform="translate(16,0)" />
                    <use href="#hex" transform="rotate(60) translate(16,0)" />
                  </g>
                  <g id="hex-pattern" transform="scale(0.333)">
                    <use href="#hex-chunk" />
                    <use href="#hex-chunk" transform="rotate(30) translate(0,48) rotate(-30)" />
                    <use href="#hex-chunk" transform="rotate(-180) translate(0,27.7) rotate(180)" />
                    <use href="#hex-chunk" transform="rotate(-120) translate(0,27.7) rotate(120)" />
                    <use href="#hex-chunk" transform="rotate(-60) translate(0,27.7) rotate(60)" />
                    <use href="#hex-chunk" transform="translate(0,27.7)" />
                    <use href="#hex-chunk" transform="rotate(60) translate(0,27.7) rotate(-60)" />
                    <use href="#hex-chunk" transform="rotate(120) translate(0,27.7) rotate(-120)" />
                  </g>
                  <g id="ball-texture" transform="translate(0,-3.5)">
                    <use href="#hex-pattern" transform="translate(-48,0)" />
                    <use href="#hex-pattern" transform="translate(-32,0)" />
                    <use href="#hex-pattern" transform="translate(-16,0)" />
                    <use href="#hex-pattern" transform="translate(0,0)" />
                    <use href="#hex-pattern" transform="translate(16,0)" />
                  </g>
                </defs>
                <filter id="ball-shadow-inside"><feGaussianBlur in="SourceGraphic" stdDeviation="2" /></filter>
                <filter id="ball-shadow-outside"><feGaussianBlur in="SourceGraphic" stdDeviation="1" /></filter>
                <g transform="translate(28,28)">
                  <g className="pl__stripe-dot-group" fill="hsl(344,75%,40%)">
                    <circle className="pl__stripe-dot" transform="rotate(32) translate(-18.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(87) translate(-18.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(103) translate(-18.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(138) translate(-18.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(228) translate(-18.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(243) translate(-18.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(328) translate(-18.25,0)" />
                  </g>
                  <g className="pl__stripe-dot-group" fill="white">
                    <circle className="pl__stripe-dot" transform="rotate(41) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(77) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(92) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(146) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(175) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(293) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(314) translate(-15.75,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(340) translate(-15.75,0)" />
                  </g>
                  <g className="pl__stripe-dot-group" fill="hsl(213,81%,21%)">
                    <circle className="pl__stripe-dot" transform="rotate(20) translate(-13.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(55) translate(-13.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(77) translate(-13.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(106) translate(-13.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(128) translate(-13.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(174) translate(-13.25,0)" />
                    <circle className="pl__stripe-dot" transform="rotate(279) translate(-13.25,0)" />
                  </g>
                  <g fill="none" strokeLinecap="round" strokeWidth="2.5" transform="rotate(-90)">
                    <g className="pl__stripe-rotate">
                      <circle className="pl__stripe pl__stripe--1" r="18.25" stroke="hsl(344,75%,40%)" strokeDasharray="114.7 114.7" />
                    </g>
                    <g className="pl__stripe-rotate">
                      <circle className="pl__stripe pl__stripe--2" r="15.75" stroke="white" strokeDasharray="106.8 106.8" />
                    </g>
                    <g className="pl__stripe-rotate">
                      <circle className="pl__stripe pl__stripe--3" r="13.25" stroke="hsl(213,81%,21%)" strokeDasharray="99 99" />
                    </g>
                  </g>
                  <g className="pl__ball" transform="translate(0,-15.75)">
                    <circle className="pl__ball-shadow" filter="url(#ball-shadow-outside)" fill="hsla(210,10%,10%,0.5)" r="8" cx="1" cy="1" />
                    <circle fill="white" r="8" />
                    <g clipPath="url(#ball-clip)">
                      <use className="pl__ball-texture" href="#ball-texture" />
                    </g>
                    <circle className="pl__ball-shadow" clipPath="url(#ball-clip)" filter="url(#ball-shadow-inside)" fill="none" stroke="hsla(210,10%,10%,0.3)" strokeWidth="5" r="12" cx="-4" cy="-4" />
                  </g>
                </g>
              </svg>
            </div>
            <div className="pl-stripe-bar" />
            <div className="pl-label">
              <span className="pl-title">{label}</span>
              <span className="pl-subtitle">
                Please wait<span className="pl-dots"><span>.</span><span>.</span><span>.</span></span>
              </span>
            </div>
            <div className="pl-progress-wrap">
              <div className="pl-progress-meta">
                <span>Progress</span>
                <span ref={pctRef}>0%</span>
              </div>
              <div className="pl-progress-track">
                <div className="pl-progress-fill" />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
  